/**
 * useShellHandouts - handouts (v8.363.10): the drawer, the lightbox and the reveal.
 *
 * The reveal's vox line is posted by the GM's own seat, so it reaches the
 * audience through the same rule every other line does. Off (a null
 * context) outside a session, like the vox it rides on.
 *
 * Returns `{ handoutsApi, handoutsDrawer, handoutsContextValue,
 * viewedHandout, setViewingHandout, handoutFileUrl, handleImportHandouts }`.
 * Extracted from DeathWatchSimulator.
 */
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import * as multiplayer from '../multiplayer';
import { dismissalOf, handoutChatMessage, isDismissed, presented } from '../engine/handouts';
import { toDoc as chatToDoc } from '../engine/chat';
import { randomToken } from '../utils/entropy';
import useHandouts from './useHandouts';
import useHandoutSeen from './useHandoutSeen';
import useCurtain, { useCurtainBorrow } from './useCurtain.js';
import { diag, warnIfRejected } from '../diagnostics/log';
import { originOf } from '../engine/assets/provenance';
import { useStableApi } from './useStableCallback';

const NO_PEOPLE = [];

const DRAWER = {
  heightKey: 'handoutsHeight',
  collapsedKey: 'handoutsCollapsed',
  containerTestId: 'handouts-drawer',
  defaultCollapsed: true,
};

/** The reveal's vox line, from this seat. */
function useAnnounce(sessionId, voxMe) {
  return useCallback(
    async (h, audience) => {
      if (!sessionId || !voxMe) return;
      const ts = Date.now();
      const msg = handoutChatMessage(h, audience, voxMe, ts, () => `${ts}-${voxMe.uid.slice(0, 8)}-${randomToken(4)}`);
      await multiplayer.addChatMessage(sessionId, msg.id, chatToDoc(msg));
    },
    [sessionId, voxMe],
  );
}

/** A handout file's URL, resolved through the session (refused outside one). */
function useHandoutFileUrl(sessionId) {
  return useCallback(
    (h, field) =>
      sessionId
        ? multiplayer.handoutFileUrl(sessionId, h.id, field, h[field])
        : Promise.reject(new Error('no session')),
    [sessionId],
  );
}

/** Where this browser keeps the presentation it closed in a session. */
const closedKey = (storeKey) => `dw-handout-closed:${storeKey}`;

/** The presentation this browser closed in that session, or null (none, unreadable, or no session). */
function readClosed(storeKey) {
  if (!storeKey) return null;
  try {
    const saved = JSON.parse(window.localStorage.getItem(closedKey(storeKey)) ?? 'null');
    return typeof saved?.id === 'string' && typeof saved?.at === 'number' ? saved : null;
  } catch (err) {
    diag.warn('handouts', 'could not read the closed presentation', { message: String(err) });
    return null;
  }
}

function writeClosed(storeKey, dismissed) {
  if (!storeKey) return;
  try {
    window.localStorage.setItem(closedKey(storeKey), JSON.stringify(dismissed));
  } catch (err) {
    // A browser that refuses storage still closes it for now; only a reload shows it again.
    diag.warn('handouts', 'could not keep the closed presentation', { message: String(err) });
  }
}

/**
 * The lightbox: the handout this viewer opened, else the one the GM has put
 * up (v9.8.0, engine/handouts/present) unless this viewer closed it.
 *
 * Closing is the viewer's own: it dismisses THAT presentation, so a handout
 * presented again opens again. It is kept in this browser per session
 * (2026-10-28: a rejoin opened a handout the player had closed before leaving),
 * never on the server - a late joiner who never closed it still sees it.
 *
 * @param {import('../engine/handouts').Handout[]} handouts - this viewer's feed
 * @param {string|null} [storeKey] - the session, for keeping the close across a reload; none keeps it in memory
 * @returns {{ id, handout, up, isPresented, view, close }}
 */
export function useViewing(handouts, storeKey = null) {
  const [id, setId] = useState(null);
  const [dismissed, setDismissed] = useState(() => readClosed(storeKey));
  // Another session on the same mounted shell: its own close, not the last one's (the review, 2026-10-28).
  const readFor = useRef(storeKey);
  useEffect(() => {
    if (readFor.current === storeKey) return;
    readFor.current = storeKey;
    setDismissed(readClosed(storeKey));
  }, [storeKey]);
  const up = useMemo(() => presented(handouts), [handouts]);
  const showing = up && !isDismissed(up, dismissed) ? up : null;
  const opened = id ? (handouts.find((h) => h.id === id) ?? null) : null;
  const handout = opened ?? showing;
  const close = useCallback(() => {
    // Closing the one that is up, however it was opened, closes the presentation for this viewer.
    if (up && (!id || id === up.id)) {
      setDismissed(dismissalOf(up));
      writeClosed(storeKey, dismissalOf(up));
    }
    setId(null);
  }, [up, id, storeKey]);
  return useMemo(
    () => ({ id, handout, up, isPresented: !!handout && handout.id === up?.id, view: setId, close }),
    [id, handout, up, close],
  );
}

/**
 * The drawer's way into the GM's asset library (v11.82.0): an image or a
 * handout picked there is posted as a NEW handout - its bytes copied, the
 * session's quota applying - carrying the asset and version it came from.
 * Null without a library (a player, an account that cannot host).
 */
function libraryFor(tableAssets, handoutsApi) {
  if (!tableAssets) return null;
  return {
    lib: tableAssets.lib,
    uid: tableAssets.uid,
    filesAvailable: tableAssets.filesAvailable,
    pick: async (row) => {
      const file = await tableAssets.readFile(row);
      await handoutsApi.postFile(file, { title: row.name, origin: originOf(row) });
    },
  };
}

/** Length: one field per member of the context (declarative). */
export function handoutsContextOf(voxMe, { handoutsApi, handoutsDrawer, viewing, ...rest }) {
  if (!voxMe) return null;
  return {
    handouts: handoutsApi.handouts,
    usage: handoutsApi.usage,
    busy: handoutsApi.busy,
    isGm: rest.isGm,
    people: rest.voxPeople,
    channels: rest.channels,
    open: !handoutsDrawer.collapsed,
    toggle: handoutsDrawer.toggleCollapsed,
    close: () => handoutsDrawer.setCollapsed(true),
    postFiles: handoutsApi.postFiles,
    postNote: handoutsApi.postNote,
    postLink: handoutsApi.postLink,
    update: handoutsApi.update,
    reveal: handoutsApi.reveal,
    present: handoutsApi.present,
    endPresent: handoutsApi.endPresent,
    presenting: viewing.up?.id ?? null,
    unread: rest.seen.unread,
    seenOf: rest.seen.seenOf,
    remove: handoutsApi.remove,
    removeMany: handoutsApi.removeMany,
    fileUrl: rest.handoutFileUrl,
    viewing: viewing.id,
    view: viewing.view,
    closeView: viewing.close,
    library: libraryFor(rest.tableAssets, handoutsApi),
  };
}

/** @param {object} args - sessionId, isGm, voxMe, voxPeople, channels. */
/** The seen marks, with whatever is open large on this screen marked seen, however it came to be open. */
function useSeenOnOpen({ sessionId, voxMe, isGm, voxPeople, handouts, viewing }) {
  const seen = useHandoutSeen({
    sessionId,
    uid: voxMe?.uid ?? '',
    isGm,
    handouts,
    people: voxPeople ?? NO_PEOPLE,
    mp: multiplayer,
  });
  const openId = viewing.handout?.id ?? null;
  const { mark } = seen;
  useEffect(() => {
    if (openId) mark(openId);
  }, [openId, mark]);
  return seen;
}

const NO_FILE = () => Promise.reject(new Error('no library'));

/**
 * What the drawer's library reads of the table's assets (`libraryFor`): the GM's library, the account, whether
 * files can be read, and reading one. The table's assets object is rebuilt with every map edit and more; this view
 * keeps its identity until one of those four changes (plan shell-contexts, A5). Null without a library.
 */
function useLibraryView(tableAssets) {
  const view = useStableApi({
    lib: tableAssets?.lib ?? null,
    uid: tableAssets?.uid ?? null,
    filesAvailable: tableAssets?.filesAvailable ?? false,
    readFile: tableAssets?.readFile ?? NO_FILE,
  });
  return tableAssets ? view : null;
}

/** The drawer's context, rebuilt only when one of its parts changes. */
function useHandoutsContextValue(voxMe, parts) {
  const { handoutsApi, handoutsDrawer, viewing, seen, isGm, voxPeople, channels, handoutFileUrl, tableAssets } = parts;
  return useMemo(
    () =>
      handoutsContextOf(voxMe, {
        handoutsApi,
        handoutsDrawer,
        viewing,
        seen,
        isGm,
        voxPeople,
        channels,
        handoutFileUrl,
        tableAssets,
      }),
    [voxMe, handoutsApi, isGm, voxPeople, channels, handoutsDrawer, handoutFileUrl, viewing, seen, tableAssets],
  );
}

export default function useShellHandouts({ sessionId, isGm, voxMe, voxPeople, channels, tableAssets = null }) {
  const announce = useAnnounce(sessionId, voxMe);
  // The same objects until their data changes (plan shell-contexts, A4): new on every render, they gave the handouts
  // context a new value each time. Every function in either is an action.
  const handoutsApi = useStableApi(useHandouts({ sessionId, me: voxMe?.uid ?? '', isGm, mp: multiplayer, announce }));
  const handoutsDrawer = useStableApi(useCurtain({ ...DRAWER, borrow: useCurtainBorrow() }));
  const viewing = useViewing(handoutsApi.handouts, sessionId);
  const seen = useSeenOnOpen({ sessionId, voxMe, isGm, voxPeople, handouts: handoutsApi.handouts, viewing });
  const handoutFileUrl = useHandoutFileUrl(sessionId);
  // Only what the drawer's context reads of it: its resize grip is rebuilt with each render.
  const drawerView = useStableApi({
    collapsed: handoutsDrawer.collapsed,
    toggleCollapsed: handoutsDrawer.toggleCollapsed,
    setCollapsed: handoutsDrawer.setCollapsed,
  });
  const parts = {
    tableAssets: useLibraryView(tableAssets),
    handoutsApi,
    handoutsDrawer: drawerView,
    viewing,
    seen,
    isGm,
    voxPeople,
    channels,
    handoutFileUrl,
  };
  const handoutsContextValue = useHandoutsContextValue(voxMe, parts);
  const handleImportHandouts = useCallback(
    (entries) => warnIfRejected(handoutsApi.importEntries(entries), 'handouts', 'import'),
    [handoutsApi],
  );
  return {
    handoutsApi,
    handoutsDrawer,
    handoutsContextValue,
    viewedHandout: viewing.handout,
    handoutPresented: viewing.isPresented,
    setViewingHandout: viewing.view,
    closeHandoutView: viewing.close,
    endHandoutPresent: handoutsApi.endPresent,
    handoutFileUrl,
    handleImportHandouts,
  };
}
